import { createReducer, on } from '@ngrx/store';

import { TeamListStateActions, UserTeamsActions } from '../team-list/team-list-state.actions';
import { UsersStateActions } from '../users/users-state.actions';

import { UserListStateActions } from './user-list-state.actions';
import { userListInitialState, userListStateAdapter, UserListStateModel } from './user-list-state.model';

export const UserListStateReducer = createReducer(
  userListInitialState,
  on(UserListStateActions.getUserListSuccess, (state, { users }): UserListStateModel => {
    const newState = userListStateAdapter.setAll(users, state);

    const userIds = [];
    for (const i in users) {
      userIds.push(users[i]._id);
    }

    return {
      ...newState,
      filterBarUsers: ['all', ...userIds],
    };
  }),
  on(TeamListStateActions.getSelectedTeam, (state, { id }): UserListStateModel => {
    const userIds = [];
    for (const key in state.entities) {
      const user = state.entities[key];
      if (user.teamIds.includes(id)) {
        userIds.push(user._id);
      }
    }

    return {
      ...state,
      filterBarUsers: ['all', ...userIds],
    };
  }),
  on(
    UserListStateActions.setLoggedInUser,
    (state, { loggedInUser }): UserListStateModel => ({
      ...state,
      loggedInUser,
    })
  ),
  on(UserTeamsActions.addUsersToTeam, (state, { users, teamId }) => {
    const newState = { ...state };

    if (users.some(user => user._id === state.loggedInUser._id)) {
      newState.loggedInUser = {
        ...newState.loggedInUser,
        teams: [...state.loggedInUser.teams, { teamId }],
      };
    }

    const updates = users.map(user => ({
      id: user._id,
      changes: { teamIds: [...state.entities[user._id].teamIds, teamId] },
    }));
    return userListStateAdapter.updateMany(updates, newState);
  }),
  on(UserTeamsActions.removeUserFromTeam, (state, { userId, teamId }) => {
    const newState = { ...state };

    if (state.loggedInUser._id === userId) {
      newState.loggedInUser = {
        ...newState.loggedInUser,
        teams: state.loggedInUser.teams.filter(team => team.teamId !== teamId),
      };
    }
    const update = {
      id: userId,
      changes: {
        teamIds: state.entities[userId].teamIds.filter(id => id !== teamId),
      },
    };
    return userListStateAdapter.updateOne(update, newState);
  }),
  on(UserListStateActions.addItems, (state, { users }) => {
    const newState = userListStateAdapter.addMany(users, state);
    return {
      ...newState,
      filterBarUsers: state.filterBarUsers,
    };
  }),
  on(UserListStateActions.removeItem, (state, { _id }) => {
    const newState = userListStateAdapter.removeOne(_id, state);
    return {
      ...newState,
      filterBarUsers: state.filterBarUsers?.filter(id => id !== _id) ?? null,
    };
  }),
  on(UsersStateActions.updateOne, UsersStateActions.updateSuccess, (state, { _id, changes }) => {
    const update = {
      id: _id,
      changes: {
        ...(changes?.roleCode ? { roleCode: changes.roleCode } : null),
        ...(changes?.isImplementer ? { isImplementer: changes.isImplementer } : null),
        ...(changes?.teams ? { teamIds: changes.teams.map(t => t.teamId) } : null),
      },
    };
    // Only update for properties we track in this store
    if (Object.keys(update.changes).length > 0) {
      return userListStateAdapter.updateOne(update, state);
    }
    return state;
  }),
  on(
    TeamListStateActions.changeFilterBarTeam,
    UserListStateActions.resetFilterBarUsers,
    (state): UserListStateModel => {
      return {
        ...state,
        filterBarUsers: ['all', ...Object.values(state.entities).map(user => user._id)],
      };
    }
  ),
  on(UserListStateActions.changeFilterBarUsers, (state, { users, allUsers }): UserListStateModel => {
    let newIds = new Set(users.map(u => u._id));
    const currentIds = new Set(state.filterBarUsers || []);

    // Check if sets are equal
    if (newIds.size === currentIds.size && [...newIds].every(id => currentIds.has(id))) {
      return state;
    }

    if (newIds.has('all') && !currentIds.has('all')) {
      newIds = new Set(['all', ...allUsers.map(u => u._id)]);
    } else if (!newIds.has('all') && currentIds.has('all')) {
      newIds = new Set([]);
    } else {
      const realNewIds = [...newIds].filter(id => id !== 'all');

      if (realNewIds.length === allUsers.length) {
        newIds = new Set(['all', ...realNewIds]);
      } else {
        newIds = new Set([...newIds].filter(id => id !== 'all'));
      }
    }

    return {
      ...state,
      filterBarUsers: [...newIds],
    };
  })
);
